import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UsersIcon, MusicIcon, MapPinIcon, GlobeIcon, Edit, MoreVertical, Trash, LinkIcon } from 'lucide-react';

/**
 * Reusable Band Card Component
 * Used across BandsPage and ProfilePage for consistent band display
 * 
 * @param {Object} band - The band object to display
 * @param {Function} onEdit - Callback when edit is clicked
 * @param {Function} onDelete - Callback when delete is clicked
 * @param {Function} onInvite - Callback when invite is clicked
 * @param {boolean} canManage - Whether the user can manage this band (admin rights)
 * @param {string} context - Context where card is used ('bands', 'profile')
 */
const BandCard = ({ 
  band, 
  onEdit,
  onDelete,
  onInvite,
  canManage = false,
  context = 'bands'
}) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const getBandStats = () => {
    const stats = [];
    if (band.stats?.member_count || band.members?.length) {
      stats.push(`${band.stats?.member_count || band.members?.length || 0} members`);
    }
    if (band.stats?.sheet_count) stats.push(`${band.stats.sheet_count} sheets`);
    if (band.stats?.setlist_count) stats.push(`${band.stats.setlist_count} setlists`);
    return stats.join(' • ');
  };

  const handleCardClick = () => {
    navigate(`/band/${band._id}`);
  };

  return (
    <div 
      className="border border-border rounded-md p-4 bg-card hover:bg-card/80 transition-colors cursor-pointer relative"
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center overflow-hidden">
          <UsersIcon className="h-5 w-5 mr-2 flex-shrink-0 text-muted-foreground" />
          <h3 className="text-lg font-medium truncate">{band.name || 'Untitled Band'}</h3>
        </div>
        
        {/* Always show dropdown menu for consistency */}
        <div 
          className="relative" 
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowDropdown(!showDropdown);
            }}
            className="p-1 rounded-md hover:bg-background flex-shrink-0"
          >
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-border">
              {/* Standard action available to everyone */}
              <button 
                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                  navigate(`/band/${band._id}`);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                View Details
              </button>

              {/* Admin-only actions */}
              {canManage && onInvite && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onInvite(band);
                  }}
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Invite Members
                </button>
              )}

              {canManage && onEdit && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onEdit(band);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Band
                </button>
              )}

              {canManage && onDelete && (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button 
                    className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      onDelete(band);
                    }}
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete Band
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Band Description */}
      {band.description && (
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{band.description}</p>
      )}

      {/* Band Details */}
      <div className="space-y-1 text-sm text-muted-foreground mb-2">
        {band.genre && (
          <div className="flex items-center">
            <MusicIcon className="w-3 h-3 mr-1" />
            <span>{band.genre}</span>
          </div>
        )}
        {band.location && (
          <div className="flex items-center">
            <MapPinIcon className="w-3 h-3 mr-1" />
            <span>{band.location}</span>
          </div>
        )}
        {band.website && (
          <div className="flex items-center">
            <GlobeIcon className="w-3 h-3 mr-1" />
            <a 
              href={band.website} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-xs"
              onClick={(e) => e.stopPropagation()}
            >
              Website
            </a>
          </div>
        )}
      </div>

      {/* Stats and Role */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>
          {getBandStats() && <span>{getBandStats()}</span>}
        </div>
        {band.user_role && context === 'bands' && (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            band.user_role === 'admin' 
              ? 'bg-red-100 text-red-800 border border-red-300' 
              : 'bg-blue-100 text-blue-800 border border-blue-300'
          }`}>
            {band.user_role}
          </span>
        )}
      </div>

      {/* Date created */}
      <div className="mt-1 text-xs text-muted-foreground">
        {formatDate(band.createdAt || band.created_at)}
      </div>
    </div>
  );
};

export default BandCard;